import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import type {
  HeidiNote,
  HeidiOptions,
  PatientInfo,
  Region,
} from '@heidihealth/widget-types/types';
import {
  OrganisationService,
  SessionStorage,
} from '@principle-theorem/ng-principle-shared';
import { injectScript } from '@principle-theorem/ng-shared';
import {
  HEIDI_GENDER_MAP,
  IHeidiAuthenticateRequest,
  IHeidiAuthenticateResponse,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  getError,
  httpsCallable,
  serialise,
  SerialisedData,
  snapshot,
  snapshotDefined,
  WithRef,
} from '@principle-theorem/shared';
import { get } from 'lodash';
import * as moment from 'moment-timezone';

interface IHeidiAuthToken {
  token: string;
  expiresAt: string;
}
const HEIDI_SCRIPT_URL_PRODUCTION =
  'https://widget.heidihealth.com/widget/heidi.js';
const HEIDI_SCRIPT_URL_TEST =
  'https://registrar.widget.heidihealth.com/staging/widget/heidi.js';

@Injectable({
  providedIn: 'root',
})
export class HeidiWidgetService {
  private _containerId = 'heidi';
  private _org = inject(OrganisationService);
  private _snackBar = inject(MatSnackBar);
  private _document = inject(DOCUMENT);
  private _sessionStorage = new SessionStorage<Record<string, IHeidiAuthToken>>(
    'heidiTokens'
  );
  private _scriptLoaded = signal(false);
  private _loadedScriptUrl?: string;
  noteData = signal<string | undefined>(undefined);
  loading = signal(false);

  async preload(testMode = false): Promise<void> {
    const scriptUrl = testMode
      ? HEIDI_SCRIPT_URL_TEST
      : HEIDI_SCRIPT_URL_PRODUCTION;

    if (this._scriptLoaded() && this._loadedScriptUrl !== scriptUrl) {
      this._scriptLoaded.set(false);
      this._loadedScriptUrl = undefined;
    }

    if (this._scriptLoaded()) {
      return;
    }

    this._ensureContainerExists();

    try {
      await injectScript('heidi-sdk', scriptUrl);
      if (!window.Heidi) {
        throw new Error('Heidi widget script tag exists, but SDK is not ready');
      }
      this._scriptLoaded.set(true);
      this._loadedScriptUrl = scriptUrl;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load Heidi widget script', getError(error));
      throw error;
    }
  }

  async open(
    patient: WithRef<IPatient>,
    email: string,
    heidiUserReference: string
  ): Promise<void> {
    this.loading.set(true);

    try {
      const practice = await snapshotDefined(this._org.practice$);
      const testMode = !!practice.metadata?.heidi?.testMode;

      if (!this._scriptLoaded()) {
        await this.preload(testMode);
      }

      const token = await this._getAuthToken(
        email,
        heidiUserReference,
        testMode
      );
      if (!token) {
        return;
      }
      this._initialiseWidget(token, testMode);

      if (window.Heidi) {
        const heidiPatient: PatientInfo = {
          id: patient.ref.id,
          name: patient.name,
          dob: patient.dateOfBirth,
          gender: HEIDI_GENDER_MAP[patient.gender],
        };
        await window.Heidi.open({ patient: heidiPatient });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to open Heidi widget', getError(error));
    } finally {
      this.loading.set(false);
    }
  }

  clearNoteData(): void {
    this.noteData.set(undefined);
  }

  private _initialiseWidget(token: string, testMode: boolean): void {
    const heidiOptions: HeidiOptions = {
      token,
      target: '#heidi',
      onReady: () => this._setupEventHandlers(),
      display: {
        maxHeight: 600,
        draggable: true,
      },
    };

    if (!testMode) {
      heidiOptions.region = 'AU' as Region;
    }

    new window.Heidi(heidiOptions);
  }

  private async _getAuthToken(
    email: string,
    heidiUserReference: string,
    testMode: boolean
  ): Promise<string | undefined> {
    const cacheKey = `${heidiUserReference}:${testMode ? 'test' : 'prod'}`;
    const now = moment();
    const existingTokens = this._sessionStorage.value || {};
    const cachedToken = get(existingTokens, cacheKey, undefined);

    if (cachedToken) {
      const expiry = moment.utc(cachedToken.expiresAt);
      if (expiry.isValid() && expiry.isAfter(now.clone().add(1, 'minute'))) {
        return cachedToken.token;
      }
    }

    const practice = await snapshotDefined(this._org.practice$);
    const payload: IHeidiAuthenticateRequest = {
      email,
      practiceRef: practice.ref,
      heidiUserReference,
    };

    const snackBar = this._snackBar.open('Initialising Heidi Health...');
    try {
      const response = await snapshot(
        httpsCallable<
          SerialisedData<IHeidiAuthenticateRequest>,
          IHeidiAuthenticateResponse
        >('http-heidi-authenticate')(serialise(payload))
      );
      this._sessionStorage.update({
        ...existingTokens,
        [cacheKey]: {
          token: response.token,
          expiresAt: response.expiration_time,
        },
      });

      snackBar.dismiss();
      return response.token;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Heidi Authentication Failed', getError(error));
      snackBar.dismiss();
      this._snackBar.open(
        'Failed to initialise Heidi Health. Please try again later.'
      );
    }
  }

  private _setupEventHandlers(): void {
    window.Heidi.onPushData((data: HeidiNote) => {
      if (data.noteData) {
        this.noteData.set(data.noteData);
      }
    });
  }

  private _ensureContainerExists(): void {
    if (this._document.getElementById(this._containerId)) {
      return;
    }

    const container = this._document.createElement('div');
    container.id = this._containerId;
    container.classList.add('print:hidden');
    this._document.body.appendChild(container);
  }
}
